import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { PASSWORD_POLICY_DESCRIPTION, passwordPolicyError } from "@/lib/passwordPolicy";

const PENDING_INVITE_KEY = "dental_os_pending_invite_code";
export const PENDING_JOIN_SIGNUP_KEY = "dental_os_pending_join_signup";

// =====================================================================
// Public /join page — accepts ?code=XXX-XXX-XXX
// Flow:
//   1. Read code from URL; if missing, show a code input.
//   2. Preview invitation (anon RPC) → show clinic/role + inviter.
//   3. If not signed in: let user create account OR sign in.
//      Email is LOCKED to the invited address (per spec).
//   4. accept_invitation(code, full_name) — race-safe.
//   5. Refresh session so hook picks up active_clinic_id; redirect to /dashboard.
// =====================================================================

export default function JoinClinic() {
  const [params, setParams]   = useSearchParams();
  const { session, signUp, signIn, signOut, refreshClinics } = useAuth();
  const navigate              = useNavigate();

  const [codeInput, setCodeInput] = useState(params.get("code") ?? "");
  const [preview, setPreview]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError]     = useState(null);

  const [mode, setMode]           = useState("create"); // 'create' | 'signin'
  const [password, setPassword]   = useState("");
  const [fullName, setFullName]   = useState("");
  const [displayName, setDisplayName] = useState("");
  const [prcLicense, setPrcLicense] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [accepted, setAccepted]   = useState(null);
  const [verificationEmail, setVerificationEmail] = useState("");

  useEffect(() => {
    const code = params.get("code");
    if (!code) return;
    window.sessionStorage.setItem(PENDING_INVITE_KEY, code);
    (async () => {
      setPreviewLoading(true); setPreviewError(null);
      const { data, error } = await supabase.rpc("preview_invitation", { p_code: code });
      setPreviewLoading(false);
      if (error) { setPreviewError(error.message); return; }
      if (data?.ok === false) { setPreviewError(data.message); return; }
      setPreview(data);
      const invitedName = data?.invitee_full_name || "";
      const invitedDisplay = data?.invitee_display_name || invitedName;
      setFullName((current) => current || invitedName);
      setDisplayName((current) => current || invitedDisplay);
    })();
  }, [params]);

  async function handleEnterCode(e) {
    e.preventDefault();
    const code = codeInput.trim().toUpperCase();
    if (!/^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{3}$/.test(code)) {
      setPreviewError("Enter a valid code in the format XXX-XXX-XXX.");
      return;
    }
    setParams({ code });
  }

  async function handleAccept(e) {
    e.preventDefault();
    setSubmitError(null); setSubmitting(true);

    if (session && preview?.email) {
      const signedInEmail = (session.user?.email || "").trim().toLowerCase();
      const invitedEmail = (preview.email || "").trim().toLowerCase();
      if (signedInEmail && invitedEmail && signedInEmail !== invitedEmail) {
        setSubmitError(
          `This invitation was sent to ${preview.email}, but you are signed in as ${session.user.email}. Please sign out and use the invited address.`
        );
        setSubmitting(false);
        return;
      }
    }

    if (isClinicalRole(preview?.role)) {
      if (!fullName.trim()) {
        setSubmitError("Please enter your full name.");
        setSubmitting(false);
        return;
      }
      if (!displayName.trim()) {
        setSubmitError("Please enter the doctor's display name.");
        setSubmitting(false);
        return;
      }
      if (!prcLicense.trim()) {
        setSubmitError("Please enter the doctor's PRC number.");
        setSubmitting(false);
        return;
      }
    }

    if (!session) {
      if (mode === "create") {
        const policyError = passwordPolicyError(password);
        if (policyError) { setSubmitError(policyError); setSubmitting(false); return; }
        if (!fullName.trim())    { setSubmitError("Please enter your full name.");             setSubmitting(false); return; }
        const pendingJoin = {
          code: params.get("code"),
          email: preview.email,
          fullName: fullName.trim(),
          displayName: displayName.trim(),
          prcLicense: prcLicense.trim(),
          createdAt: new Date().toISOString(),
        };
        window.localStorage.setItem(PENDING_JOIN_SIGNUP_KEY, JSON.stringify(pendingJoin));
        const up = await signUp(preview.email, password, {
          emailRedirectTo: `${window.location.origin}/auth/confirmed?intent=join&code=${encodeURIComponent(params.get("code") || "")}`,
          data: { full_name: fullName.trim() },
        });
        if (up.error) { setSubmitError(up.error.message); setSubmitting(false); return; }
        if (!up.data?.session) {
          setVerificationEmail(preview.email);
          setSubmitting(false);
          return;
        }
      } else {
        const si = await signIn(preview.email, password);
        if (si.error) { setSubmitError(si.error.message); setSubmitting(false); return; }
      }
    }

    const { data, error } = await supabase.rpc("accept_invitation", {
      p_code:         params.get("code"),
      p_full_name:    fullName || null,
      p_display_name: displayName || null,
      p_prc_license:  prcLicense || null,
    });
    if (error) { setSubmitError(error.message); setSubmitting(false); return; }
    // Refresh JWT so `active_clinic_id` + `clinic_role` get baked in.
    await supabase.auth.refreshSession();
    // refreshSession fires onAuthStateChange but keeps the same user.id,
    // so AuthContext's [session?.user?.id]-keyed effect does NOT re-fetch
    // the clinic list. Without this explicit refresh, ProtectedRoute
    // sees clinics=[] and bounces the brand-new invitee to the
    // create-clinic wizard instead of their dashboard.
    await refreshClinics();
    window.sessionStorage.removeItem(PENDING_INVITE_KEY);
    window.localStorage.removeItem(PENDING_JOIN_SIGNUP_KEY);
    setSubmitting(false);
    setAccepted(data);
    setTimeout(() => navigate("/dashboard", { replace: true }), 900);
  }

  // ---- UI states -------------------------------------------------------
  if (accepted) {
    return (
      <AuthLayout title={`You're in!`} subtitle={`Welcome to ${accepted.clinic_name}.`}>
        <div className="flex items-center gap-3 text-emerald-700" data-testid="join-success">
          <CheckCircle2 className="w-5 h-5"/>
          <p className="text-sm">Redirecting to your dashboard…</p>
        </div>
      </AuthLayout>
    );
  }

  if (!params.get("code")) {
    return (
      <AuthLayout title="Join a clinic" subtitle="Enter the 9-character code from your invitation email.">
        <form onSubmit={handleEnterCode} className="space-y-4" data-testid="join-code-form">
          <input
            data-testid="join-code-input"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            placeholder="ABC-DEF-GHJ"
            className="w-full rounded-lg border border-slate-200 px-3 py-3 text-center text-lg font-mono tracking-widest focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
            maxLength={11}
          />
          {previewError ? <p data-testid="join-error" className="text-sm text-rose-600">{previewError}</p> : null}
          <button data-testid="join-continue-button" type="submit"
                  className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2.5">
            Continue
          </button>
        </form>
      </AuthLayout>
    );
  }

  if (previewLoading) {
    return (
      <AuthLayout title="Checking your invitation…">
        <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>One moment.</div>
      </AuthLayout>
    );
  }

  if (previewError) {
    return (
      <AuthLayout title="We can't use that invitation" subtitle={" "}>
        <div data-testid="join-preview-error" className="flex items-start gap-3 text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 shrink-0"/>
          <p className="text-sm">{previewError}</p>
        </div>
        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-teal-700 hover:underline">Back to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  if (!preview) return null;

  if (!session && verificationEmail) {
    return (
      <AuthLayout title="Check your email" subtitle="Verify your email to join the clinic.">
        <div data-testid="join-verify-email" className="space-y-3">
          <p className="text-sm leading-6 text-slate-600">
            We sent a verification link to <span className="font-semibold text-slate-900">{verificationEmail}</span>.
            Click it to confirm your email, then Dental OS will finish adding you to the clinic.
          </p>
          <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
            The link expires after one hour. If it expires or was already used, ask the clinic admin to resend your invitation.
          </p>
        </div>
      </AuthLayout>
    );
  }

  const signedInEmail = (session?.user?.email || "").trim().toLowerCase();
  const invitedEmail = (preview.email || "").trim().toLowerCase();
  const signedInAsWrongEmail = !!(session && signedInEmail && invitedEmail && signedInEmail !== invitedEmail);
  const clinicalInvite = isClinicalRole(preview.role);

  return (
    <AuthLayout
      title={`You're invited to join ${preview.clinic_name}`}
      subtitle={`${preview.invited_by_name ?? "The clinic admin"} invited you as ${roleLabel(preview.role)}.`}
    >
      <form onSubmit={handleAccept} className="space-y-4" data-testid="join-accept-form">
        <div data-testid="join-invite-summary" className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-sm text-slate-700">
          Email: <span className="font-medium">{preview.email}</span>
        </div>

        {!session ? (
          <>
            <div className="flex gap-2 text-xs border-b border-slate-200">
              <button type="button" data-testid="join-mode-create"
                      onClick={() => setMode("create")}
                      className={`px-3 py-2 border-b-2 -mb-px ${mode === "create" ? "border-teal-600 text-teal-700 font-semibold" : "border-transparent text-slate-500"}`}>
                Create account
              </button>
              <button type="button" data-testid="join-mode-signin"
                      onClick={() => setMode("signin")}
                      className={`px-3 py-2 border-b-2 -mb-px ${mode === "signin" ? "border-teal-600 text-teal-700 font-semibold" : "border-transparent text-slate-500"}`}>
                I already have an account
              </button>
            </div>

            {mode === "create" ? (
              <>
                <FullNameField value={fullName} onChange={(v) => {
                  setFullName(v);
                  if (!displayName.trim()) setDisplayName(v);
                }} />
                {clinicalInvite ? (
                  <>
                    <DisplayNameField value={displayName} onChange={setDisplayName} />
                    <PrcField value={prcLicense} onChange={setPrcLicense} />
                  </>
                ) : null}
              </>
            ) : null}

            {mode === "signin" && clinicalInvite ? (
              <>
                <FullNameField value={fullName} onChange={(v) => {
                  setFullName(v);
                  if (!displayName.trim()) setDisplayName(v);
                }} />
                <DisplayNameField value={displayName} onChange={setDisplayName} />
                <PrcField value={prcLicense} onChange={setPrcLicense} />
              </>
            ) : null}

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="join-password">Password</label>
              <input id="join-password" data-testid="join-password-input" type="password" required
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                     placeholder={mode === "create" ? "10+ chars, Aa, 1, symbol" : ""} />
              {mode === "create" ? <p className="mt-1 text-xs text-slate-500">{PASSWORD_POLICY_DESCRIPTION}</p> : null}
            </div>
          </>
        ) : (
          <div data-testid="join-already-signed-in" className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              You're signed in. Accepting will add this clinic to your account.
            </p>
            {signedInAsWrongEmail ? (
              <div className="rounded-lg border border-rose-100 bg-rose-50 p-3">
                <p className="text-sm text-rose-700 leading-relaxed">
                  This invitation was sent to <span className="font-medium">{preview.email}</span>, but you are signed in as <span className="font-medium">{session.user.email}</span>.
                </p>
                <button
                  type="button"
                  data-testid="join-signout-button"
                  onClick={async () => {
                    await signOut();
                    setPassword("");
                    setFullName("");
                    setDisplayName("");
                    setPrcLicense("");
                    setSubmitError(null);
                    setMode("create");
                  }}
                  className="mt-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-rose-700 border border-rose-200 hover:bg-rose-100"
                >
                  Sign out and use invited email
                </button>
              </div>
            ) : null}
            {!signedInAsWrongEmail && clinicalInvite ? (
              <>
                <FullNameField value={fullName} onChange={(v) => {
                  setFullName(v);
                  if (!displayName.trim()) setDisplayName(v);
                }} />
                <DisplayNameField value={displayName} onChange={setDisplayName} />
                <PrcField value={prcLicense} onChange={setPrcLicense} />
              </>
            ) : null}
          </div>
        )}

        {submitError ? <p data-testid="join-submit-error" className="text-sm text-rose-600">{submitError}</p> : null}

        <button data-testid="join-accept-button" type="submit" disabled={submitting || signedInAsWrongEmail}
                className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold py-2.5">
          {submitting ? "Joining…" : `Join ${preview.clinic_name}`}
        </button>
      </form>
    </AuthLayout>
  );
}

function roleLabel(r) {
  return ({ clinic_admin: "Clinic Admin", doctor: "Doctor", receptionist: "Receptionist", assistant: "Assistant" })[r] ?? r;
}

function isClinicalRole(role) {
  return role === "doctor";
}

function FullNameField({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="join-name">Full name</label>
      <input
        id="join-name"
        data-testid="join-fullname-input"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
      />
    </div>
  );
}

function DisplayNameField({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="join-display-name">Display name</label>
      <input
        id="join-display-name"
        data-testid="join-display-name-input"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Dr. Cameron"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
      />
      <p className="mt-1 text-xs text-slate-500">This is the doctor name patients see on bookings, treatment plans, and clinic documents.</p>
    </div>
  );
}

function PrcField({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="join-prc">PRC #</label>
      <input
        id="join-prc"
        data-testid="join-prc-input"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 1234567"
        maxLength={60}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
      />
      <p className="mt-1 text-xs text-slate-500">This appears on treatment plans and doctor-issued clinic documents.</p>
    </div>
  );
}
